import {
    STOREFRONT_VALUES_KEYS,
    STOREFRONT_VALUES_NAMESPACES,
} from "@/constants/storefront_values";
import { CommonModule } from "@modules/common_module/CommonModule";
import type { DesignModeProviderInterface } from "@modules/common_module/DesignModeProvider/DesignModeProviderInterface";
import StorefrontValuesManager from "@modules/storefront_values/StorefrontValuesManager/StorefrontValuesManager";
import { StorefrontValuesModule } from "@modules/storefront_values/StorefrontValuesModule";
import type { Observable } from "rxjs";
import { map, of } from "rxjs";
import { singleton } from "tsyringe";

import { QR_CODE } from "../../QRCode/constants";
import RewardsStorefrontValuesBrandingSchema from "../models/RewardsStorefrontValuesBrandingSchema";
import RewardsStorefrontValuesCustomersEligibilitySettingsSchema from "../models/RewardsStorefrontValuesCustomersEligibilitySettingsSchema";
import type {
    RewardsStorefrontValuesBrandingEntity,
    RewardsStorefrontValuesCustomersEligibilitySettingsEntity,
    RewardsStorefrontValuesPosSettingsEntity,
    RewardsStorefrontValuesProgramsStatusEntity,
    RewardsStorefrontValuesRewardPageAvailabilityEntity,
    RewardsStorefrontValuesSettingsEntity,
} from "../models/RewardsStorefrontValuesEntities";
import RewardsStorefrontValuesRewardPageAvailabilitySchema from "../models/RewardsStorefrontValuesPageAvailabilitySchema";
import RewardsStorefrontValuesPosSettingsSchema from "../models/RewardsStorefrontValuesPosSettingsSchema";
import RewardsStorefrontValuesProgramsStatusSchema from "../models/RewardsStorefrontValuesProgramsStatusSchema";
import { RewardsStorefrontValuesSettingsSchema } from "../models/RewardsStorefrontValuesSettingsSchema";

@singleton()
class RewardsStorefrontValuesService {
    private readonly storefrontValuesManager: StorefrontValuesManager;
    private readonly designModeProvider: DesignModeProviderInterface;

    constructor(
        private readonly storefrontValuesModule: StorefrontValuesModule,
        private readonly commonModule: CommonModule
    ) {
        this.storefrontValuesManager =
            this.storefrontValuesModule.exports.storefrontValuesManager;
        this.designModeProvider = this.commonModule.exports.designModeProvider;
    }

    //Rewards programs status
    public selectRewardsProgramsStatus(): Observable<RewardsStorefrontValuesProgramsStatusEntity> {
        if (this.designModeProvider.isDesignMode()) {
            return of({
                pointsProgramEnabled: true,
                referralProgramEnabled: true,
                tiersProgramEnabled: true,
                b2bProgramEnabled: false,
            });
        }

        return this.storefrontValuesManager.selectValue({
            namespace: STOREFRONT_VALUES_NAMESPACES.REWARDS_CRITICAL,
            key: STOREFRONT_VALUES_KEYS.REWARDS_PROGRAMS_STATUS,
            schema: RewardsStorefrontValuesProgramsStatusSchema,
        });
    }

    public selectRewardsProgramStatus(
        program: keyof RewardsStorefrontValuesProgramsStatusEntity
    ): Observable<boolean> {
        return this.selectRewardsProgramsStatus().pipe(
            map((programStatus) => programStatus[program])
        );
    }

    //Rewards settings
    public selectRewardsSettings(): Observable<RewardsStorefrontValuesSettingsEntity> {
        return this.storefrontValuesManager.selectValue({
            namespace: STOREFRONT_VALUES_NAMESPACES.REWARDS_CRITICAL,
            key: STOREFRONT_VALUES_KEYS.REWARDS_SETTINGS,
            schema: RewardsStorefrontValuesSettingsSchema,
        });
    }

    public selectRewardsGeneralSettings(): Observable<
        RewardsStorefrontValuesSettingsEntity["generalSettings"]
    > {
        return this.selectRewardsSettings().pipe(
            map((settings) => settings.generalSettings)
        );
    }

    public selectRewardsReferralShareChannels(): Observable<
        RewardsStorefrontValuesSettingsEntity["referralShareChannels"]
    > {
        return this.selectRewardsSettings().pipe(
            map((settings) =>
                settings.referralShareChannels.filter(
                    (channel) => channel.isEnabled
                )
            )
        );
    }

    //Rewards page widgets availability
    public selectRewardsPageWidgetsAvailability(): Observable<RewardsStorefrontValuesRewardPageAvailabilityEntity> {
        return this.storefrontValuesManager.selectValue({
            namespace: STOREFRONT_VALUES_NAMESPACES.REWARDS_CRITICAL,
            key: STOREFRONT_VALUES_KEYS.REWARDS_REWARD_PAGE_WIDGETS_AVAILABILITY,
            schema: RewardsStorefrontValuesRewardPageAvailabilitySchema,
        });
    }

    //Rewards customers eligibility
    public selectRewardsCustomersEligibilitySettings(): Observable<RewardsStorefrontValuesCustomersEligibilitySettingsEntity> {
        return this.storefrontValuesManager.selectValue({
            namespace: STOREFRONT_VALUES_NAMESPACES.REWARDS_CRITICAL,
            key: STOREFRONT_VALUES_KEYS.REWARDS_CUSTOMERS_ELIGIBILITY,
            schema: RewardsStorefrontValuesCustomersEligibilitySettingsSchema,
        });
    }

    //Rewards branding
    public selectRewardsBranding(): Observable<RewardsStorefrontValuesBrandingEntity> {
        return this.storefrontValuesManager.selectValue({
            namespace: STOREFRONT_VALUES_NAMESPACES.REWARDS_CRITICAL,
            key: STOREFRONT_VALUES_KEYS.REWARDS_BRANDING,
            schema: RewardsStorefrontValuesBrandingSchema,
        });
    }

    //Rewards pos settings
    public selectRewardsPosSettings(): Observable<RewardsStorefrontValuesPosSettingsEntity> {
        if (this.designModeProvider.isDesignMode()) {
            return of({
                codeType: QR_CODE.barcode_2d,
                isEnabled: true,
            });
        }

        return this.storefrontValuesManager.selectValue({
            namespace: STOREFRONT_VALUES_NAMESPACES.REWARDS_CRITICAL,
            key: STOREFRONT_VALUES_KEYS.REWARDS_POS_SETTINGS,
            schema: RewardsStorefrontValuesPosSettingsSchema,
        });
    }
}

export default RewardsStorefrontValuesService;
